import { EventName, EventSource, ParentCategoryName } from '@loyalty/shared-common';
import {
  EventReportDetailsProperties,
  PeriodicityLimitationEnum,
  RuleType,
} from '@loyalty/shared-common/rule-api-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IncomingWebhookAuthenticationType,
  IncomingWebhookType,
} from '@loyalty/shared-common/ingestor-api-client';
import { ReactNode, useMemo } from 'react';
import { WebhookEventTypes } from '@loyalty/shared-common/integration-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { faBullhorn } from '@fortawesome/pro-solid-svg-icons/faBullhorn';
import { faCartShopping } from '@fortawesome/pro-solid-svg-icons/faCartShopping';
import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown';
import { faDice } from '@fortawesome/pro-solid-svg-icons/faDice';
import { faPeopleArrows } from '@fortawesome/pro-solid-svg-icons/faPeopleArrows';
import { useTranslation } from 'react-i18next';

export const ruleTableColumns = {
  drag: {
    width: 35,
  },
  actions: {
    width: 50,
  },
  source: {
    width: 125,
  },
  currencies: {
    width: 125,
  },
};

type translator = (translationKey: string) => string;

const parentCategoryNameTranslations = {
  [ParentCategoryName.legal_rules]: (t: translator) => t('enums.EventSource.groupName.legal_rules'),
  [ParentCategoryName.newsletter_marketing]: (t: translator) =>
    t('enums.EventSource.groupName.newsletter_marketing'),
  [ParentCategoryName.other]: (t: translator) => t('enums.EventSource.groupName.other'),
  [ParentCategoryName.partners]: (t: translator) => t('enums.EventSource.groupName.partners'),
};

export function translateParentCategory(
  t: (translationKey: string) => string,
  val: ParentCategoryName,
) {
  return parentCategoryNameTranslations[val](t);
}

export function useEventSourceNames() {
  const { t } = useTranslation();
  return useMemo<{ [key in EventSource]: string }>(
    () => ({
      [EventSource.QUALIFIO_LOYALTY]: t('enums.EventSource.QUALIFIO_LOYALTY'),
      [EventSource.QUALIFIO_ENGAGE]: t('enums.EventSource.QUALIFIO_ENGAGE'),
      [EventSource.ADVANCED_FORMAT]: t('enums.EventSource.ADVANCED_FORMAT'),
      [EventSource.ECOMMERCE]: t('enums.EventSource.ECOMMERCE'),
      [EventSource.BINARY]: t('enums.EventSource.BINARY'),
      [EventSource.REFERRAL]: t('enums.EventSource.REFERRAL'),
    }),
    [t],
  );
}

export function useEventSourcePeriodicity() {
  const { t } = useTranslation();
  return useMemo<{ [key in PeriodicityLimitationEnum]: string }>(
    () => ({
      [PeriodicityLimitationEnum.FOREVER]: t(
        'earning-rules.options.periodicityLimitation.options.FOREVER',
      ),
      [PeriodicityLimitationEnum.HOURS_24]: t(
        'earning-rules.options.periodicityLimitation.options.HOURS_24',
      ),
      [PeriodicityLimitationEnum.DAYS_7]: t(
        'earning-rules.options.periodicityLimitation.options.DAYS_7',
      ),
      [PeriodicityLimitationEnum.DAYS_30]: t(
        'earning-rules.options.periodicityLimitation.options.DAYS_30',
      ),
      [PeriodicityLimitationEnum.DAYS_365]: t(
        'earning-rules.options.periodicityLimitation.options.DAYS_365',
      ),
      [PeriodicityLimitationEnum.DAILY]: t(
        'earning-rules.options.periodicityLimitation.options.DAILY',
      ),
      [PeriodicityLimitationEnum.WEEKLY]: t(
        'earning-rules.options.periodicityLimitation.options.WEEKLY',
      ),
      [PeriodicityLimitationEnum.MONTHLY]: t(
        'earning-rules.options.periodicityLimitation.options.MONTHLY',
      ),
      [PeriodicityLimitationEnum.YEARLY]: t(
        'earning-rules.options.periodicityLimitation.options.YEARLY',
      ),
    }),
    [t],
  );
}

export function useEventSourceValues() {
  const { t } = useTranslation();
  return useMemo<{ [key in EventSource]: string }>(
    () => ({
      [EventSource.QUALIFIO_LOYALTY]: t('earning-rules.properties.eventSources.QUALIFIO_LOYALTY'),
      [EventSource.QUALIFIO_ENGAGE]: t('earning-rules.properties.eventSources.QUALIFIO_ENGAGE'),
      [EventSource.ADVANCED_FORMAT]: t('earning-rules.properties.eventSources.ADVANCED_FORMAT'),
      [EventSource.ECOMMERCE]: t('earning-rules.properties.eventSources.ECOMMERCE'),
      [EventSource.BINARY]: t('earning-rules.properties.eventSources.BINARY'),
      [EventSource.REFERRAL]: t('earning-rules.properties.eventSources.REFERRAL'),
    }),
    [t],
  );
}

export function useEventSourceValuesValue() {
  const { t } = useTranslation();
  return useMemo<{ [key in EventName]: string }>(
    () =>
      ({
        [EventName.PROFILE_CREATION]: t(
          'earning-rules.properties.eventNames.QUALIFIO_LOYALTY.PROFILE_CREATION',
        ),
        [EventName.BIRTHDAY]: t('earning-rules.properties.eventNames.QUALIFIO_LOYALTY.BIRTHDAY'),
        [EventName.MEMBER_VISIT]: t(
          'earning-rules.properties.eventNames.QUALIFIO_LOYALTY.MEMBER_VISIT',
        ),
        [EventName.MEMBER_CLICK]: t(
          'earning-rules.properties.eventNames.QUALIFIO_LOYALTY.MEMBER_CLICK',
        ),
        [EventName.LEVEL_CHANGE]: t(
          'earning-rules.properties.eventNames.QUALIFIO_LOYALTY.LEVEL_CHANGE',
        ),
        [EventName.CAMPAIGN]: t('earning-rules.properties.eventNames.QUALIFIO_ENGAGE.CAMPAIGN'),
        [EventName.PREDICTION]: t('earning-rules.properties.eventNames.ADVANCED_FORMAT.PREDICTION'),
        [EventName.PURCHASE]: t('earning-rules.properties.eventSources.ECOMMERCE.PURCHASE'),
        [EventName.BASIC]: t('earning-rules.properties.eventNames.BINARY.BASIC'),
        [EventName.BURNING]: t('earning-rules.properties.eventNames.BURNING'),
        [EventName.ECOMMERCE]: t('earning-rules.properties.eventNames.REFERRAL.ECOMMERCE'),
      } as any),
    [t],
  );
}

export function useRuleRTELabel() {
  const { t } = useTranslation();
  return useMemo<{ [key in RuleType]: string }>(
    () => ({
      [RuleType.EARN]: t('earning-rules.placeholders.description'),
      [RuleType.BURN]: t('burning-rules.placeholders.description'),
      [RuleType.ACCESS]: t('access-rules.placeholders.description'),
    }),
    [t],
  );
}

export function useEventPropertiesTranslations() {
  const { t } = useTranslation();
  return useMemo<{ [key in EventReportDetailsProperties]: string }>(
    () => ({
      id: t('event.report.details.properties.id'),
      eventSource: t('event.report.details.properties.eventSource'),
      eventName: t('event.report.details.properties.eventName'),
      programId: t('event.report.details.properties.programId'),
      programName: t('event.report.details.properties.programName'),
      clientEventId: t('event.report.details.properties.clientEventId'),
      eventTimestamp: t('event.report.details.properties.eventTimestamp'),
      eventCreatedAt: t('event.report.details.properties.eventCreatedAt'),
      createdAt: t('event.report.details.properties.createdAt'),
      eventId: t('event.report.details.properties.eventId'),
      status: t('event.report.details.properties.status'),
      isRepublished: t('event.report.details.properties.republished'),
    }),
    [t],
  );
}

export function useIncomingWebhookAuthType() {
  const { t } = useTranslation();
  return useMemo<{ [key in IncomingWebhookAuthenticationType]: string }>(
    () => ({
      [IncomingWebhookAuthenticationType.HEADER]: t(
        'incoming-webhooks.enums.authentication.type.HEADER',
      ),
      [IncomingWebhookAuthenticationType.QUERY_PARAM]: t(
        'incoming-webhooks.enums.authentication.type.QUERY_PARAM',
      ),
      [IncomingWebhookAuthenticationType.HUB_SIGNATURE]: t(
        'incoming-webhooks.enums.authentication.type.HUB_SIGNATURE',
      ),
    }),
    [t],
  );
}

export function useIncomingWebhookType() {
  const { t } = useTranslation();
  return useMemo<{ [key in IncomingWebhookType]: string }>(
    () => ({
      [IncomingWebhookType.MAPPED_EVENT]: t('incoming-webhooks.enums.type.MAPPED_EVENT'),
    }),
    [t],
  );
}

export enum IdentityProperties {
  PICTURE = 'picture',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  LANGUAGE = 'language',
  BIRTHDAY = 'birthday',
  ADDRESS_STREET = 'address.street',
  ADDRESS_NUMBER = 'address.number',
  ADDRESS_ZIPCODE = 'address.zipCode',
  ADDRESS_COUNTRY = 'address.country',
  ADDRESS_BOX = 'address.box',
  ADDRESS_STATE = 'address.state',
  ADDRESS_CITY = 'address.city',
  PHONE = 'phone',
  GENDER = 'gender',
}

export function useIdentityProperties() {
  const { t } = useTranslation();
  return useMemo<{ [key in IdentityProperties]: string }>(
    () => ({
      [IdentityProperties.PICTURE]: t('profiles.details.identity.properties.picture'),
      [IdentityProperties.FIRST_NAME]: t('profiles.details.identity.properties.firstName'),
      [IdentityProperties.LAST_NAME]: t('profiles.details.identity.properties.lastName'),
      [IdentityProperties.EMAIL]: t('profiles.details.identity.properties.email'),
      [IdentityProperties.LANGUAGE]: t('profiles.details.identity.properties.language'),
      [IdentityProperties.BIRTHDAY]: t('profiles.details.identity.properties.birthday'),
      [IdentityProperties.ADDRESS_STREET]: t('profiles.details.identity.properties.address.street'),
      [IdentityProperties.ADDRESS_NUMBER]: t('profiles.details.identity.properties.address.number'),
      [IdentityProperties.ADDRESS_ZIPCODE]: t(
        'profiles.details.identity.properties.address.zipCode',
      ),
      [IdentityProperties.ADDRESS_COUNTRY]: t(
        'profiles.details.identity.properties.address.country',
      ),
      [IdentityProperties.ADDRESS_BOX]: t('profiles.details.identity.properties.address.box'),
      [IdentityProperties.ADDRESS_STATE]: t('profiles.details.identity.properties.address.state'),
      [IdentityProperties.ADDRESS_CITY]: t('profiles.details.identity.properties.address.city'),
      [IdentityProperties.PHONE]: t('profiles.details.identity.properties.phone'),
      [IdentityProperties.GENDER]: t('profiles.details.identity.properties.gender'),
    }),
    [t],
  );
}

export function useWebhookEventTypes() {
  const { t } = useTranslation();
  return useMemo<{ [key in WebhookEventTypes]: string }>(
    () => ({
      [WebhookEventTypes.All]: t('outgoing-webhooks.events.all'),
      [WebhookEventTypes.ProfileCreated]: t('outgoing-webhooks.events.profile_created'),
      [WebhookEventTypes.LevelChanged]: t('outgoing-webhooks.events.level_changed'),
      [WebhookEventTypes.PointsEarned]: t('outgoing-webhooks.events.points_earned'),
      [WebhookEventTypes.PointsBurned]: t('outgoing-webhooks.events.points_burned'),
      [WebhookEventTypes.PointsAdded]: t('outgoing-webhooks.events.points_added'),
      [WebhookEventTypes.PointsSubtracted]: t('outgoing-webhooks.events.points_subtracted'),
      [WebhookEventTypes.RewardOffered]: t('outgoing-webhooks.events.reward_offered'),
      [WebhookEventTypes.IdentitiesCreated]: t('outgoing-webhooks.events.identities_created'),
      [WebhookEventTypes.ProfileDeleted]: t('outgoing-webhooks.events.profile_deleted'),
      [WebhookEventTypes.MembershipDeleted]: t('outgoing-webhooks.events.membership_deleted'),
      [WebhookEventTypes.PointsExpired]: t('outgoing-webhooks.events.points_expired'),
    }),
    [t],
  );
}

export const eventSourceIcons: { [key in EventSource]: ReactNode } = {
  [EventSource.QUALIFIO_LOYALTY]: <FontAwesomeIcon icon={faCrown} />,
  [EventSource.QUALIFIO_ENGAGE]: (
    <FontAwesomeIcon icon={faBullhorn} color={colorPalette.orange.main} />
  ),
  [EventSource.ADVANCED_FORMAT]: <FontAwesomeIcon icon={faDice} />,
  [EventSource.ECOMMERCE]: <FontAwesomeIcon icon={faCartShopping} color={colorPalette.blue.main} />,
  [EventSource.BINARY]: <FontAwesomeIcon icon={faCrown} color={colorPalette.red.main} />,
  [EventSource.REFERRAL]: <FontAwesomeIcon icon={faPeopleArrows} />,
};
